'use client';

import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { format } from 'date-fns';
import Link from 'next/link';

interface Todo {
  id: string;
  title: string;
  details: string | null;
  notes: string | null;
  due_date: string | null;
  delegate: string | null;
  sort_order: number;
  deleted_at: string | null;
  archived_at: string | null;
  decision_id: string | null;
  suggestion_reason: string | null;
  confidence: number | null;
  sensitive: boolean;
}

interface TodoItemProps {
  todo: Todo;
  onDelete: (id: string) => void;
  onRestore?: (id: string) => void;
  onPurge?: (id: string) => void;
  onMoveToTop?: (id: string) => void;
  onUpdateNotes?: (id: string, notes: string) => void;
  onClick?: (todo: Todo) => void;
  compact?: boolean;
  showDragHandle?: boolean;
  showActions?: boolean;
  showAssist?: boolean;
  isArchived?: boolean;
}

export default function TodoItem({
  todo,
  onDelete,
  onRestore,
  onPurge,
  onMoveToTop,
  onUpdateNotes,
  onClick,
  compact = false,
  showDragHandle = true,
  showActions = true,
  showAssist = false,
  isArchived = false,
}: TodoItemProps) {
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(todo.notes || '');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const handleSaveNotes = async () => {
    if (!onUpdateNotes) return;
    setIsSavingNotes(true);
    try {
      await onUpdateNotes(todo.id, notesValue);
      setIsEditingNotes(false);
    } finally {
      setIsSavingNotes(false);
    }
  };
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: todo.id, disabled: isArchived });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isDeleted = !!todo.deleted_at;
  const isSensitive = todo.sensitive;

  // Wrap content with blur for sensitive items
  if (isSensitive) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`group relative flex items-start gap-3 px-3 py-2 rounded-lg border transition-colors cursor-pointer ${
          isDeleted
            ? 'bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800'
            : 'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700'
        }`}
        onClick={() => onClick?.(todo)}
      >
        <div className="blur-sm pointer-events-none select-none flex-1">
          <p className={`text-sm ${compact ? 'line-clamp-3' : 'truncate'} ${
            isDeleted
              ? 'line-through text-zinc-300'
              : 'text-zinc-900 dark:text-zinc-100'
          }`}>
            {todo.title}
          </p>
          {!compact && todo.details && (
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2">
              {todo.details}
            </p>
          )}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-black/5 dark:bg-white/5 rounded-lg pointer-events-none">
          <span className="text-zinc-400 dark:text-zinc-500 font-medium text-sm px-3 py-1 bg-white/80 dark:bg-zinc-900/80 rounded pointer-events-auto">
            Sensitive
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-start gap-1 px-3 py-2 rounded-lg border transition-colors ${
        isDeleted
          ? 'bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800'
          : 'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700'
      }`}
    >
      {/* Drag Handle & Move to Top */}
      {showDragHandle && !isArchived && (
        <div className="flex flex-col justify-between self-stretch">
          <button
            {...attributes}
            {...listeners}
            className="touch-none p-1 cursor-grab active:cursor-grabbing text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
            aria-label="Drag to reorder"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </svg>
          </button>
          {/* Move to top arrow - bottom of left column */}
          {!compact && !isDeleted && onMoveToTop && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveToTop(todo.id);
              }}
              className="p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              aria-label="Move to top"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5h14M12 19V9m0 0l-4 4m4-4l4 4" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div
        className={`flex-1 min-w-0 ${onClick ? 'cursor-pointer' : ''}`}
        onClick={() => onClick?.(todo)}
      >
        <p className={`text-sm ${compact ? 'line-clamp-3' : 'truncate'} ${
          isDeleted
            ? 'line-through text-zinc-300'
            : 'text-zinc-900 dark:text-zinc-100'
        }`}>
          {todo.title}
        </p>
        {!compact && todo.details && (
          <p className={`mt-1 text-sm whitespace-pre-wrap ${
            isDeleted ? 'text-zinc-500 dark:text-zinc-400' : 'text-zinc-600 dark:text-zinc-400'
          }`}>
            {todo.details}
          </p>
        )}
        {!compact && (todo.due_date || todo.delegate || todo.decision_id) && (
          <div className="flex flex-wrap gap-3 mt-1">
            {todo.due_date && (
              <span className={`text-xs ${
                isDeleted ? 'text-zinc-400' : 'text-zinc-500 dark:text-zinc-400'
              }`}>
                Due: {format(new Date(todo.due_date + 'T12:00:00'), 'MMM d')}
              </span>
            )}
            {todo.delegate && (
              <span className={`text-xs ${
                isDeleted ? 'text-zinc-400' : 'text-zinc-500 dark:text-zinc-400'
              }`}>
                {todo.delegate.split(/[,\s]+/).filter(Boolean).map(d => `@${d.replace(/^@/, '')}`).join(', ')}
              </span>
            )}
            {todo.decision_id && (
              <a
                href={`/decisions/${todo.decision_id}`}
                onClick={(e) => e.stopPropagation()}
                className={`text-xs ${
                  isDeleted ? 'text-zinc-400' : 'text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300'
                }`}
              >
                View decision →
              </a>
            )}
          </div>
        )}
        {!compact && todo.suggestion_reason && (
          <p className={`mt-2 text-xs italic ${
            isDeleted ? 'text-zinc-500 dark:text-zinc-500' : 'text-blue-500 dark:text-blue-400'
          }`}>
            Why: {todo.suggestion_reason}
          </p>
        )}
        {!compact && todo.confidence != null && todo.confidence > 0 && (
          <p className={`mt-1 text-xs ${
            isDeleted ? 'text-zinc-400' :
            todo.confidence >= 80 ? 'text-green-500 dark:text-green-400' :
            todo.confidence >= 50 ? 'text-yellow-600 dark:text-yellow-400' :
            'text-orange-500 dark:text-orange-400'
          }`}>
            AI Confidence: {todo.confidence}%
          </p>
        )}
        {/* Notes section - for delegates to log progress */}
        {!compact && !isDeleted && !isArchived && onUpdateNotes && (
          <div className="mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-800" onClick={(e) => e.stopPropagation()}>
            {isEditingNotes ? (
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <textarea
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setIsEditingNotes(false);
                      setNotesValue(todo.notes || '');
                    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      handleSaveNotes();
                    }
                  }}
                  placeholder="Add notes about progress, blockers, or updates..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-700 rounded-lg
                           bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                           focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent
                           placeholder-zinc-400 dark:placeholder-zinc-500"
                  autoFocus
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingNotes(false);
                      setNotesValue(todo.notes || '');
                    }}
                    className="px-4 py-2 text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveNotes();
                    }}
                    disabled={isSavingNotes}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-500 dark:bg-blue-500 dark:text-zinc-900 dark:hover:bg-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSavingNotes ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {todo.notes ? (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingNotes(true);
                    }}
                    className="cursor-pointer group/notes"
                  >
                    <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">Notes:</p>
                    <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap group-hover/notes:bg-zinc-50 dark:group-hover/notes:bg-zinc-800 rounded px-2 py-1 -mx-2 -my-1">
                      {todo.notes}
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingNotes(true);
                    }}
                    className="text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                  >
                    + Add notes
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {/* Display notes in read-only mode for completed/archived items */}
        {!compact && (isDeleted || isArchived) && todo.notes && (
          <div className="mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-800">
            <p className="text-xs font-medium text-zinc-400 dark:text-zinc-500 mb-1">Notes:</p>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 whitespace-pre-wrap">
              {todo.notes}
            </p>
          </div>
        )}
      </div>

      {/* Actions - hidden on small screens for mobile usability */}
      {showActions && (
      <div className="hidden sm:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {isArchived ? (
          <>
            {onPurge && (
              <button
                onClick={() => onPurge(todo.id)}
                className="inline-flex items-center gap-1 px-2 py-1 mr-2 text-sm text-red-500 hover:text-red-600 dark:text-red-400"
                aria-label="Permanently delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Purge
              </button>
            )}
            {onRestore && (
              <button
                onClick={() => onRestore(todo.id)}
                className="inline-flex items-center gap-1 px-2 py-1 text-sm text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-600"
                aria-label="Restore todo"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                Restore
              </button>
            )}
          </>
        ) : isDeleted ? (
          <>
            {onPurge && (
              <button
                onClick={() => onPurge(todo.id)}
                className="inline-flex items-center gap-1 px-2 py-1 mr-2 text-sm text-red-500 hover:text-red-600 dark:text-red-400"
                aria-label="Permanently delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Purge
              </button>
            )}
            {onRestore && (
              <button
                onClick={() => onRestore(todo.id)}
                className="inline-flex items-center gap-1 px-2 py-1 text-sm text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-600"
                aria-label="Restore todo"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                Restore
              </button>
            )}
          </>
        ) : (
          <>
            {showAssist && (
              <Link
                href={`/todos/assist/${todo.id}`}
                onClick={(e) => e.stopPropagation()}
                className={`inline-flex items-center gap-1 text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-600 ${compact ? 'p-1' : 'px-2 py-1 text-sm'}`}
                aria-label="Get AI assistance"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {!compact && <span className="hidden min-[1000px]:inline">Assist</span>}
              </Link>
            )}
            <button
              onClick={() => onDelete(todo.id)}
              className={`inline-flex items-center gap-1 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-600 cursor-pointer ${compact ? 'p-1' : 'px-2 py-1 text-sm'}`}
              aria-label="Mark as done"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {!compact && <span className="hidden min-[1000px]:inline">Done</span>}
            </button>
          </>
        )}
      </div>
      )}
    </div>
  );
}
